// src/site/pages/Projects.js
import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Stack,
  useMediaQuery,
  SwipeableDrawer,
  IconButton,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import Grid from '@mui/material/Grid'; // Updated import
import ProjectCard from '../components/ProjectCard';

const Projects = () => {
  const [categories, setCategories] = useState([]);
  const [visibleCounts, setVisibleCounts] = useState({});
  const [activeCategory, setActiveCategory] = useState('');
  const categoryRefs = useRef({});
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isMobile = useMediaQuery('(max-width: 600px)');

  useEffect(() => {
    axios
      .get('/scripts/getProjectsWithCategories.php')
      .then((response) => {
        const data = Array.isArray(response.data) ? response.data : []; // Ensure data is an array
        setCategories(data);

        const initialCounts = data.reduce((acc, category) => {
          acc[category.category_title] = 4;
          return acc;
        }, {});
        setVisibleCounts(initialCounts);
      })
      .catch((error) => console.error('Error fetching data:', error));
  }, []);

  const handleSeeMore = (categoryTitle) => {
    setVisibleCounts((prevCounts) => ({
      ...prevCounts,
      [categoryTitle]: prevCounts[categoryTitle] + 6,
    }));
  };

  const scrollToCategory = (title) => {
    const categoryRef = categoryRefs.current[title];
    if (categoryRef) {
      window.scrollTo({
        top: categoryRef.offsetTop - 100,
        behavior: 'smooth',
      });
    }
    if (isMobile) setDrawerOpen(false);
  };

  const handleScroll = () => {
    const scrollPosition = window.scrollY + window.innerHeight / 2;
    let currentCategory = '';

    for (const [title, ref] of Object.entries(categoryRefs.current)) {
      if (ref && ref.offsetTop <= scrollPosition) {
        currentCategory = title;
      }
    }

    if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
      currentCategory = categories[categories.length - 1]?.category_title || '';
    }

    setActiveCategory(currentCategory);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [categories]);

  const categoryBackgrounds = [
    'rgba(240, 244, 195, 0.8)',
    'rgba(255, 224, 178, 0.8)',
    'rgba(179, 229, 252, 0.8)',
    'rgba(209, 196, 233, 0.8)',
    'rgba(255, 236, 179, 0.8)',
  ];

  const SidebarContent = useMemo(
    () => (
      <Stack spacing={2} alignItems="center">
        <Typography
          variant="h6"
          component="div"
          sx={{ textAlign: 'center', fontWeight: 'bold', color: '#4caf50' }}
        >
          Jump to Category
        </Typography>

        <Stack direction="column" spacing={1}>
          {categories.map((category) => (
            <Button
              key={category.category_title}
              onClick={() => scrollToCategory(category.category_title)}
              sx={{
                textTransform: 'capitalize',
                fontWeight: 'bold',
                color:
                  activeCategory === category.category_title
                    ? '#388e3c'
                    : '#4caf50',
                backgroundColor:
                  activeCategory === category.category_title
                    ? 'rgba(76, 175, 80, 0.2)'
                    : 'transparent',
                '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' },
              }}
            >
              {category.category_title}
            </Button>
          ))}
        </Stack>
      </Stack>
    ),
    [categories, activeCategory]
  );

  return (
    <Box
      sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#fafafa' }}
    >
      {isMobile && (
        <IconButton
          sx={{ position: 'fixed', top: 10, left: 10, zIndex: 1300 }}
          onClick={() => setDrawerOpen(true)}
        >
          <MenuIcon />
        </IconButton>
      )}

      {isMobile ? (
        <SwipeableDrawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
        >
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{ alignSelf: 'flex-end', m: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {SidebarContent}
        </SwipeableDrawer>
      ) : (
        <Box
          sx={{
            width: '300px',
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            overflowY: 'auto',
            backgroundColor: '#ffffff',
            borderRight: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '2px 0px 8px rgba(0, 0, 0, 0.1)',
            padding: 2,
          }}
        >
          {SidebarContent}
        </Box>
      )}

      <Container
        maxWidth="lg"
        sx={{
          mt: 6,
          ml: isMobile ? 0 : '350px',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            mb: 6,
            textAlign: 'center',
            color: '#444',
            textTransform: 'uppercase',
            letterSpacing: 2,
          }}
        >
          My Projects
        </Typography>

        {categories.map((category, index) => (
          <Box
            key={category.category_title}
            ref={(el) => (categoryRefs.current[category.category_title] = el)}
            sx={{
              mb: 8,
              p: 4,
              borderRadius: 3,
              background:
                categoryBackgrounds[index % categoryBackgrounds.length],
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(10px)',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                mb: 3,
                color: '#444',
                textTransform: 'capitalize',
                borderBottom: '2px solid #4caf50',
                display: 'inline-block',
                pb: 1,
              }}
            >
              {category.category_title}
            </Typography>

            <Grid container spacing={1}>
              {category.projects
                .slice(0, visibleCounts[category.category_title])
                .map((project) => (
                  <Grid item xs={6} sm={4} md={3} key={project.id}>
                    <ProjectCard project={project} />
                  </Grid>
                ))}
            </Grid>

            {category.projects.length >
              visibleCounts[category.category_title] && (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={() => handleSeeMore(category.category_title)}
                  sx={{
                    backgroundColor: '#4caf50',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#388e3c' },
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    px: 4,
                  }}
                >
                  See More
                </Button>
              </Box>
            )}
          </Box>
        ))}
      </Container>
    </Box>
  );
};

export default Projects;
